import React, { useState, useEffect, useCallback } from 'react';
import TeamManagement from './TeamManagement';
import AccountDetails from './AccountDetails';
import AccountsTable from './AccountsTable';
import MetaPerformance from './MetaPerformance';
import GooglePerformance from './GooglePerformance';
import SyncLogs from './SyncLogs';
import TelegramReports from './TelegramReports';
import ClientPortal from './ClientPortal';
import ClientAccessManager from './ClientAccessManager';
import MetaFormLinker from './MetaFormLinker';
import CommandPalette from './CommandPalette';
import AlertsPanel from './AlertsPanel';
import HealthOverview from './HealthOverview';
import AIChatWindow from './AIChatWindow';
import ApiCredentials from './ApiCredentials';
import { API_URL } from '../config';
import { formatCurrency, formatNumber } from '../utils/formatters';

// Campaign Objective Configuration
// NOTE: Engagement & Messages campaigns both generate LEADS (messaging leads)
const OBJECTIVE_CONFIG = {
  'OUTCOME_LEADS': { name: 'Lead Form', color: 'bg-blue-500', textColor: 'text-blue-400', icon: '📝', metric: 'CPL', resultType: 'form_leads' },
  'LEAD_GENERATION': { name: 'Lead Form', color: 'bg-blue-500', textColor: 'text-blue-400', icon: '📝', metric: 'CPL', resultType: 'form_leads' },
  'OUTCOME_TRAFFIC': { name: 'Traffic', color: 'bg-purple-500', textColor: 'text-purple-400', icon: '🔗', metric: 'CPC', resultType: 'traffic' },
  'LINK_CLICKS': { name: 'Traffic', color: 'bg-purple-500', textColor: 'text-purple-400', icon: '🔗', metric: 'CPC', resultType: 'traffic' },
  'WEBSITE_TRAFFIC': { name: 'Traffic', color: 'bg-purple-500', textColor: 'text-purple-400', icon: '🔗', metric: 'CPC', resultType: 'traffic' },
  'OUTCOME_ENGAGEMENT': { name: 'WhatsApp', color: 'bg-green-500', textColor: 'text-green-400', icon: '💬', metric: 'CPMC', resultType: 'messaging' },
  'POST_ENGAGEMENT': { name: 'WhatsApp', color: 'bg-green-500', textColor: 'text-green-400', icon: '💬', metric: 'CPMC', resultType: 'messaging' },
  'MESSAGES': { name: 'Messages', color: 'bg-green-500', textColor: 'text-green-400', icon: '💬', metric: 'CPMC', resultType: 'messaging' },
  'OUTCOME_SALES': { name: 'Sales', color: 'bg-orange-500', textColor: 'text-orange-400', icon: '🛒', metric: 'ROAS', resultType: 'sales' },
  'CONVERSIONS': { name: 'Conversions', color: 'bg-orange-500', textColor: 'text-orange-400', icon: '🛒', metric: 'CPA', resultType: 'sales' },
  'PRODUCT_CATALOG_SALES': { name: 'Catalog', color: 'bg-orange-500', textColor: 'text-orange-400', icon: '🛒', metric: 'ROAS', resultType: 'sales' },
  'OUTCOME_AWARENESS': { name: 'Awareness', color: 'bg-cyan-500', textColor: 'text-cyan-400', icon: '📢', metric: 'CPM', resultType: 'awareness' },
  'BRAND_AWARENESS': { name: 'Awareness', color: 'bg-cyan-500', textColor: 'text-cyan-400', icon: '📢', metric: 'CPM', resultType: 'awareness' },
  'REACH': { name: 'Reach', color: 'bg-cyan-500', textColor: 'text-cyan-400', icon: '📢', metric: 'CPP', resultType: 'awareness' },
  'VIDEO_VIEWS': { name: 'Video', color: 'bg-red-500', textColor: 'text-red-400', icon: '🎬', metric: 'CPV', resultType: 'awareness' },
  'OUTCOME_APP_PROMOTION': { name: 'App', color: 'bg-indigo-500', textColor: 'text-indigo-400', icon: '📱', metric: 'CPI', resultType: 'app' },
  'APP_INSTALLS': { name: 'App', color: 'bg-indigo-500', textColor: 'text-indigo-400', icon: '📱', metric: 'CPI', resultType: 'app' },
  'SEARCH': { name: 'Search', color: 'bg-yellow-500', textColor: 'text-yellow-400', icon: '🔍', metric: 'CPC', resultType: 'traffic' },
  'PERFORMANCE_MAX': { name: 'PMax', color: 'bg-teal-500', textColor: 'text-teal-400', icon: '⚡', metric: 'CPA', resultType: 'other' },
};

const getObjectiveConfig = (objective) => {
  return OBJECTIVE_CONFIG[objective] || { name: 'Campaign', color: 'bg-zinc-500', textColor: 'text-zinc-400', icon: '📊', metric: 'CPR', resultType: 'other' };
};

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = {
    success: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const styles = {
    success: 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30',
    error: 'bg-red-500/10 text-red-400 border-red-500/30',
    info: 'bg-[#6366f1]/10 text-[#6366f1] border-[#6366f1]/30'
  };

  // Handle message being an object with {type, message} structure
  const displayMessage = typeof message === 'object' && message !== null
    ? (message.message || JSON.stringify(message))
    : message;

  return (
    <div className={`fixed top-4 right-4 ${styles[type]} px-4 py-3 rounded-xl border flex items-center gap-3 animate-slide-in z-50 backdrop-blur-xl shadow-2xl`}>
      {icons[type]}
      <p className="font-medium text-sm">{displayMessage}</p>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100 transition-opacity">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// Health Status Badge
const HealthBadge = ({ status, score, size = 'sm' }) => {
  const styles = {
    healthy: 'bg-[#22c55e]/15 text-[#22c55e] ring-[#22c55e]/30',
    warning: 'bg-yellow-500/15 text-yellow-400 ring-yellow-500/30',
    critical: 'bg-red-500/15 text-red-400 ring-red-500/30',
    inactive: 'bg-zinc-500/15 text-zinc-500 ring-zinc-500/30',
    unknown: 'bg-zinc-500/15 text-zinc-500 ring-zinc-500/30'
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base'
  };

  return (
    <span className={`${styles[status] || styles.unknown} ${sizeClasses[size]} rounded-full font-bold inline-flex items-center gap-1.5 ring-1`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'healthy' ? 'bg-[#22c55e]' : status === 'warning' ? 'bg-yellow-400' : status === 'critical' ? 'bg-red-400' : 'bg-zinc-500'} animate-pulse`}></span>
      {score ? `${Math.round(score)}%` : status}
    </span>
  );
};

// Objective Badge
const ObjectiveBadge = ({ objective }) => {
  const config = getObjectiveConfig(objective);
  return (
    <span className={`${config.color}/20 ${config.textColor} px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1`}>
      <span>{config.name}</span>
    </span>
  );
};

// Platform Icon with proper styling
const PlatformIcon = ({ platform, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
    xl: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };

  if (platform === 'META') {
    return (
      <div className={`${sizeClasses[size]} bg-[#1877F2] rounded-xl flex items-center justify-center shadow-lg shadow-[#1877F2]/20`}>
        <svg className={`${iconSizes[size]} text-white`} viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} bg-white rounded-xl flex items-center justify-center shadow-lg shadow-white/10`}>
      <svg className={iconSizes[size]} viewBox="0 0 24 24" fill="none">
        <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
        <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
        <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
        <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
      </svg>
    </div>
  );
};

// Campaign Row Component
const CampaignRow = ({ campaign, currency }) => {
  const objMetrics = campaign.objective_metrics || {};
  
  const getPrimaryMetric = () => {
    // For messaging campaigns, show messaging leads
    if (objMetrics.result_type === 'messaging') {
      const msgLeads = objMetrics.messaging_leads || campaign.messaging_conversations || 0;
      return { label: 'Messages', value: msgLeads, isLead: true };
    }
    // For lead form campaigns, show form leads
    if (objMetrics.result_type === 'form_leads') {
      const formLeads = objMetrics.form_leads || campaign.leads || 0;
      return { label: 'Form Leads', value: formLeads, isLead: true };
    }
    // Default to primary_result from backend
    if (objMetrics.primary_result !== undefined) {
      return { label: objMetrics.primary_metric || 'Results', value: objMetrics.primary_result };
    }
    if (campaign.leads > 0) return { label: 'Leads', value: campaign.leads, isLead: true };
    if (campaign.purchases > 0) return { label: 'Purchases', value: campaign.purchases };
    return { label: 'Clicks', value: campaign.clicks };
  };

  const getCostMetric = () => {
    // For messaging campaigns, show CPMC
    if (objMetrics.result_type === 'messaging' && objMetrics.cost_per_messaging_lead) {
      return { label: 'CPMC', value: objMetrics.cost_per_messaging_lead };
    }
    // For lead form campaigns, show CPL
    if (objMetrics.result_type === 'form_leads' && objMetrics.cost_per_form_lead) {
      return { label: 'CPL', value: objMetrics.cost_per_form_lead };
    }
    // Default
    if (objMetrics.cost_per_result) {
      return { label: objMetrics.cost_metric || 'CPR', value: objMetrics.cost_per_result };
    }
    if (campaign.leads > 0 && campaign.spend > 0) {
      return { label: 'CPL', value: campaign.spend / campaign.leads };
    }
    if (campaign.clicks > 0 && campaign.spend > 0) {
      return { label: 'CPC', value: campaign.spend / campaign.clicks };
    }
    return { label: 'CPC', value: campaign.cpc || 0 };
  };

  const primary = getPrimaryMetric();
  const cost = getCostMetric();
  
  // Frequency status for creative fatigue
  const frequency = objMetrics.frequency || 0;
  const freqStatus = objMetrics.frequency_status || 'unknown';

  const healthColor = campaign.health_score >= 70 ? 'border-[#22c55e]/20' : 
                      campaign.health_score >= 40 ? 'border-yellow-500/20' : 'border-red-500/20';

  return (
    <div className={`bg-[#0D0D0F] rounded-xl p-3 sm:p-4 hover:bg-[#111113] transition-colors border ${healthColor} group`}>
      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <ObjectiveBadge objective={campaign.objective} />
          <HealthBadge status={campaign.health_status} score={campaign.health_score} />
          {frequency > 0 && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              freqStatus === 'critical' ? 'bg-red-500/20 text-red-400' :
              freqStatus === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-zinc-800 text-zinc-400'
            }`}>
              Freq: {frequency.toFixed(1)}
            </span>
          )}
        </div>
        <h4 className="text-sm font-semibold text-white truncate group-hover:text-[#22c55e] transition-colors mb-3">{campaign.name}</h4>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[9px] text-zinc-500 uppercase">{primary.label}</p>
            <p className={`text-sm font-bold font-mono ${primary.isLead ? 'text-[#22c55e]' : 'text-white'}`}>{formatNumber(primary.value)}</p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[9px] text-zinc-500 uppercase">{cost.label}</p>
            <p className="text-sm font-bold text-white font-mono">{formatCurrency(cost.value, currency)}</p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[9px] text-zinc-500 uppercase">Spend</p>
            <p className="text-sm font-bold text-white font-mono">{formatCurrency(campaign.spend, currency)}</p>
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <ObjectiveBadge objective={campaign.objective} />
              <HealthBadge status={campaign.health_status} score={campaign.health_score} />
              {frequency > 0 && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  freqStatus === 'critical' ? 'bg-red-500/20 text-red-400' :
                  freqStatus === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-zinc-800 text-zinc-400'
                }`}>
                  Freq: {frequency.toFixed(1)}
                </span>
              )}
            </div>
            <h4 className="text-sm font-semibold text-white truncate group-hover:text-[#22c55e] transition-colors">{campaign.name}</h4>
            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {formatNumber(campaign.impressions)}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                {formatNumber(campaign.clicks)}
              </span>
              <span className="text-zinc-600">CTR {campaign.ctr?.toFixed(2) || 0}%</span>
            </div>
          </div>
          <div className="flex items-center gap-4 md:gap-6 text-right shrink-0">
            <div className="text-center min-w-[50px] md:min-w-[60px]">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{primary.label}</p>
              <p className={`text-base md:text-lg font-bold font-mono ${primary.isLead ? 'text-[#22c55e]' : 'text-white'}`}>{formatNumber(primary.value)}</p>
            </div>
            <div className="text-center min-w-[50px] md:min-w-[60px]">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{cost.label}</p>
              <p className="text-base md:text-lg font-bold text-white font-mono">{formatCurrency(cost.value, currency)}</p>
            </div>
            <div className="text-center min-w-[60px] md:min-w-[70px]">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Spend</p>
              <p className="text-base md:text-lg font-bold text-white font-mono">{formatCurrency(campaign.spend, currency)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Expandable Account Card - Enhanced
// eslint-disable-next-line no-unused-vars
const AccountCard = ({ account, onAnalyze, isExpanded, onToggle, teamMembers, onSaveSettings, onSaveAssignment, addToast, balanceData }) => {
  const { metrics, campaigns = [] } = account;
  const isMeta = account.platform === 'META';
  
  const campaignsByObjective = campaigns.reduce((acc, camp) => {
    const obj = camp.objective || 'OTHER';
    if (!acc[obj]) acc[obj] = [];
    acc[obj].push(camp);
    return acc;
  }, {});

  const healthColor = account.health_status === 'healthy' ? 'border-[#22c55e]/30 hover:border-[#22c55e]/50' :
                      account.health_status === 'warning' ? 'border-yellow-500/30 hover:border-yellow-500/50' :
                      account.health_status === 'critical' ? 'border-red-500/30 hover:border-red-500/50' :
                      'border-white/10 hover:border-white/20';

  // Calculate total leads (form + messaging)
  const formLeads = metrics.form_leads || 0;
  const messagingLeads = metrics.messaging_leads || 0;
  const totalLeads = metrics.leads || (formLeads + messagingLeads);
  const hasMultipleLeadTypes = formLeads > 0 && messagingLeads > 0;
  
  // Frequency status
  const frequency = metrics.frequency || 0;
  const freqStatus = metrics.frequency_status || 'unknown';

  // Balance status badge
  const getBalanceStatusBadge = () => {
    if (!balanceData || balanceData.balance === null) return null;
    const statusColors = {
      healthy: 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30',
      warning: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      critical: 'bg-red-500/10 text-red-400 border-red-500/30',
    };
    return (
      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${statusColors[balanceData.balance_status] || statusColors.healthy}`}>
        {formatCurrency(balanceData.balance)}
      </span>
    );
  };

  return (
    <div className={`bg-[#09090B] rounded-2xl border transition-colors ${
      isExpanded ? 'border-[#6366f1]/50 ring-2 ring-[#6366f1]/20 shadow-lg shadow-[#6366f1]/5' : healthColor
    }`}>
      {/* Account Header */}
      <div 
        className="p-3 sm:p-5 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <div className="relative flex-shrink-0">
              <PlatformIcon platform={account.platform} size="lg" />
              {account.health_status === 'critical' && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full ring-2 ring-[#09090B] animate-pulse" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <h3 className="font-bold text-white text-sm sm:text-lg truncate max-w-[150px] sm:max-w-none">{account.name}</h3>
                <span className={`hidden sm:inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                  isMeta ? 'bg-[#1877F2]/20 text-[#1877F2]' : 'bg-white/10 text-white'
                }`}>
                  {account.platform}
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-zinc-500 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {account.campaign_count} campaigns
                </span>
                <span className="text-zinc-700">•</span>
                <HealthBadge status={account.health_status} score={account.health_score} />
                {/* Balance Badge for Meta accounts */}
                {isMeta && balanceData && balanceData.balance !== null && (
                  <>
                    <span className="text-zinc-700">•</span>
                    {getBalanceStatusBadge()}
                  </>
                )}
                {frequency > 0 && (
                  <>
                    <span className="text-zinc-700 hidden md:inline">•</span>
                    <span className={`hidden md:inline text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      freqStatus === 'critical' ? 'bg-red-500/20 text-red-400' :
                      freqStatus === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-zinc-800 text-zinc-400'
                    }`}>
                      Freq: {frequency.toFixed(1)}
                    </span>
                  </>
                )}
              </div>
              {/* Mobile health badge */}
              <div className="flex sm:hidden items-center gap-2 mt-1 flex-wrap">
                <HealthBadge status={account.health_status} score={account.health_score} />
                <span className="text-[10px] text-zinc-500">{account.campaign_count} camps</span>
                {/* Balance Badge for Meta accounts - mobile */}
                {isMeta && balanceData && balanceData.balance !== null && getBalanceStatusBadge()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <div className="hidden md:flex items-center gap-4 lg:gap-6">
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Spend</p>
                <p className="text-lg lg:text-xl font-bold text-white font-mono">{formatCurrency(metrics.spend, account.currency)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">
                  {totalLeads > 0 ? 'Total Leads' : 'Clicks'}
                </p>
                <p className="text-lg lg:text-xl font-bold text-[#22c55e] font-mono">
                  {formatNumber(totalLeads > 0 ? totalLeads : metrics.clicks)}
                </p>
                {hasMultipleLeadTypes && (
                  <p className="text-[10px] text-zinc-500">
                    Form {formLeads} · Msg {messagingLeads}
                  </p>
                )}
              </div>
            </div>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-colors ${
              isExpanded ? 'bg-[#6366f1]/20 text-[#6366f1]' : 'bg-white/5 text-zinc-500 hover:bg-white/10'
            }`}>
              <svg 
                className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Quick Metrics Grid - Mobile & Tablet */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 md:hidden">
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[10px] sm:text-xs text-zinc-500">Spend</p>
            <p className="text-xs sm:text-sm font-bold text-white font-mono">{formatCurrency(metrics.spend, account.currency)}</p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[10px] sm:text-xs text-zinc-500">{totalLeads > 0 ? 'Leads' : 'Clicks'}</p>
            <p className="text-xs sm:text-sm font-bold text-[#22c55e] font-mono">{formatNumber(totalLeads > 0 ? totalLeads : metrics.clicks)}</p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[10px] sm:text-xs text-zinc-500">{totalLeads > 0 ? 'CPL' : 'CPC'}</p>
            <p className="text-xs sm:text-sm font-bold text-white font-mono">
              {totalLeads > 0 
                ? (metrics.cpl ? formatCurrency(metrics.cpl, account.currency) : '—')
                : (metrics.cpc ? formatCurrency(metrics.cpc, account.currency) : '—')
              }
            </p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-[10px] sm:text-xs text-zinc-500">CTR</p>
            <p className="text-xs sm:text-sm font-bold text-white font-mono">{metrics.ctr ? `${metrics.ctr}%` : '—'}</p>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-white/10 animate-fade-in">
          {/* Campaign Summary */}
          <div className="p-5 bg-[#0A0A0C]">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
              <div>
                <h4 className="text-base font-bold text-white">Campaigns by Objective</h4>
                <p className="text-xs text-zinc-500 mt-0.5">Click to filter campaigns</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onAnalyze(); }}
                className="px-4 py-2 bg-[#6366f1] text-white rounded-xl text-sm font-bold hover:bg-[#5558e8] transition-colors shadow-lg shadow-[#6366f1]/20 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Run AI Analysis
              </button>
            </div>

            {/* Objective Type Badges */}
            <div className="flex flex-wrap gap-2 mb-5">
              {Object.entries(campaignsByObjective).map(([obj, camps]) => {
                const config = getObjectiveConfig(obj);
                return (
                  <span 
                    key={obj} 
                    className={`${config.color}/20 ${config.textColor} px-3 py-1.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity`}
                  >
                    <span>{config.name}</span>
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-[10px]">{camps.length}</span>
                  </span>
                );
              })}
            </div>

            {/* Campaigns List */}
            <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
              {campaigns.length > 0 ? (
                campaigns.map(campaign => (
                  <CampaignRow 
                    key={campaign.id} 
                    campaign={campaign} 
                    currency={account.currency}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p>No active campaigns</p>
                </div>
              )}
            </div>
          </div>

          {/* Account Settings */}
          <div className="p-5 border-t border-white/10">
            <AccountDetails
              account={account}
              teamMembers={teamMembers}
              onSaveSettings={onSaveSettings}
              onSaveAssignment={onSaveAssignment}
              addToast={addToast}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Platform Section Component - Enhanced
// Sidebar Navigation
const Sidebar = ({ activeTab, setActiveTab, data, inactiveCounts, healthOverview, metaBalances }) => {
  // Calculate total inactive count for badge on performance pages
  // eslint-disable-next-line no-unused-vars
  const totalInactive = (inactiveCounts?.meta_inactive_count || 0) + (inactiveCounts?.google_inactive_count || 0);
  const rechargeNeededCount = metaBalances?.summary?.recharge_needed_count || 0;
  
  const navItems = [
    { id: 'overview', label: 'Overview', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    )},
    { id: 'alerts', label: 'AI Alerts', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    )},
    { id: 'health', label: 'Health Overview', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    )},
    { id: 'meta', label: 'Meta Performance', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ), badge: inactiveCounts?.meta_inactive_count, badgeColor: 'bg-red-500' },
    { id: 'google', label: 'Google Performance', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
        <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
        <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
        <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
        <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
      </svg>
    ), badge: inactiveCounts?.google_inactive_count, badgeColor: 'bg-red-500' },
    { id: 'recharge', label: 'Recharge Needed', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ), badge: rechargeNeededCount, badgeColor: 'bg-orange-500' },
    { id: 'client-portal', label: 'Client Portal', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    )},
    { id: 'team', label: 'Team', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )},
    { id: 'sync-logs', label: 'Sync Logs', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    )},
    { id: 'telegram-reports', label: 'Telegram Reports', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    )},
    { id: 'api-credentials', label: 'API Credentials', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
      </svg>
    ), badge: null, badgeColor: null },
  ];

  return (
    <nav className="hidden lg:flex flex-col w-64 min-h-screen bg-[#09090B] border-r border-white/5 p-4 sticky top-0">
      {/* Logo */}
      <div className="px-3 py-4 mb-6">
        <img src="/logo.png" alt="Signal by Profitcast" className="h-14 w-auto" onError={(e) => e.target.style.display='none'} />
      </div>

      {/* Nav Items */}
      <div className="space-y-1 flex-1">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            data-testid={`nav-${item.id}`}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              activeTab === item.id
                ? 'bg-[#6366f1]/15 text-[#6366f1] shadow-sm'
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {item.icon}
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge > 0 && (
              <span className={`px-2 py-0.5 ${item.badgeColor || 'bg-red-500'} text-white text-[10px] font-bold rounded-full`}>
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="mt-auto pt-6 border-t border-white/5">
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#22c55e] font-mono">{healthOverview?.summary?.healthy_count || 0}</p>
            <p className="text-[10px] text-zinc-500 uppercase">Healthy</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-yellow-400 font-mono">{(healthOverview?.summary?.warning_count || 0) + (healthOverview?.summary?.critical_count || 0)}</p>
            <p className="text-[10px] text-zinc-500 uppercase">Attention</p>
          </div>
        </div>
        <p className="text-[10px] text-zinc-600 text-center">v2.1 • AI-Powered Analytics</p>
      </div>
    </nav>
  );
};

// Mobile Bottom Navigation
const MobileNav = ({ activeTab, setActiveTab }) => {
  const navItems = [
    { id: 'overview', label: 'Overview', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    )},
    { id: 'meta', label: 'Meta', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )},
    { id: 'google', label: 'Google', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
        <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
        <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
        <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
        <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
      </svg>
    )},
    { id: 'team', label: 'Team', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )},
    { id: 'sync-logs', label: 'Sync', icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    )},
    { id: 'telegram-reports', label: 'Reports', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.248-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.476z"/>
      </svg>
    )},
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass-heavy">
      <div className="flex items-center justify-around px-2 py-3">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-colors ${
              activeTab === item.id
                ? 'text-[#6366f1] bg-[#6366f1]/10'
                : 'text-zinc-500 hover:text-white'
            }`}
          >
            {item.icon}
            <span className="text-[10px] font-medium">{item.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

// Date Range Options
const DATE_RANGES = [
  { id: 'today', label: 'Today', days: 0 },
  { id: 'yesterday', label: 'Yesterday', days: 1 },
  { id: 'last_7_days', label: 'Last 7 Days', days: 7 },
  { id: 'last_14_days', label: 'Last 14 Days', days: 14 },
  { id: 'last_30_days', label: 'Last 30 Days', days: 30 },
  { id: 'last_60_days', label: 'Last 60 Days', days: 60 },
  { id: 'last_90_days', label: 'Last 90 Days', days: 90 },
  { id: 'this_month', label: 'This Month', days: -1 },
  { id: 'last_month', label: 'Last Month', days: -2 },
];

const getDateRangeLabel = (rangeId) => {
  const range = DATE_RANGES.find(r => r.id === rangeId);
  if (!range) return 'Last 7 Days';
  
  const today = new Date();
  const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  
  if (range.id === 'today') {
    return `Today (${formatDate(today)})`;
  } else if (range.id === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return `Yesterday (${formatDate(yesterday)})`;
  } else if (range.id === 'this_month') {
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return `${formatDate(startOfMonth)} - ${formatDate(today)}`;
  } else if (range.id === 'last_month') {
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return `${formatDate(startOfLastMonth)} - ${formatDate(endOfLastMonth)}`;
  } else {
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - range.days);
    return `${formatDate(startDate)} - ${formatDate(today)}`;
  }
};

// Date Range Selector Component
const DateRangeSelector = ({ selectedRange, onRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentRange = DATE_RANGES.find(r => r.id === selectedRange) || DATE_RANGES[2];
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#18181B] border border-white/10 rounded-xl text-xs sm:text-sm font-medium hover:border-[#6366f1]/50 transition-colors"
      >
        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="text-white">{currentRange.label}</span>
        <span className="text-zinc-500 text-[10px] sm:text-xs hidden md:inline">({getDateRangeLabel(selectedRange)})</span>
        <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-[#18181B] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
            <div className="p-1.5 sm:p-2 max-h-[60vh] overflow-y-auto">
              {DATE_RANGES.map(range => (
                <button
                  key={range.id}
                  onClick={() => { onRangeChange(range.id); setIsOpen(false); }}
                  className={`w-full flex items-center justify-between px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm transition-colors ${
                    selectedRange === range.id
                      ? 'bg-[#6366f1]/20 text-[#22c55e]'
                      : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="font-medium">{range.label}</span>
                  {selectedRange === range.id && (
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---- Client Portal Admin wrapper (Clients tab + Access Manager tab) ----
const ClientPortalAdmin = ({ addToast }) => {
  const [portalTab, setPortalTab] = useState('clients');

  const handleManageForms = () => {
    setPortalTab('forms');
  };

  return (
    <div className="space-y-5">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-black/30 border border-white/5 rounded-2xl p-1 w-fit flex-wrap">
        {[
          { id: 'clients', label: 'Clients & Logins' },
          { id: 'access', label: 'Portal Access Control' },
          { id: 'forms', label: 'Lead Form Setup' },
        ].map(t => (
          <button key={t.id} onClick={() => setPortalTab(t.id)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
              portalTab === t.id ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      {portalTab === 'clients' && <ClientPortal addToast={addToast} />}
      {portalTab === 'access' && <ClientAccessManager onManageForms={handleManageForms} />}
      {portalTab === 'forms' && <MetaFormLinker />}
    </div>
  );
};

// Main Dashboard Component
const Dashboard = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [healthOverview, setHealthOverview] = useState(null); // Health data from /api/health-overview
  const [metaBalances, setMetaBalances] = useState(null); // Meta account balance data
  const [googleBudgetConcerns, setGoogleBudgetConcerns] = useState(null); // Google budget concern data
  const [anomalies, setAnomalies] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery] = useState('');
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncingNow, setSyncingNow] = useState(false);
  const [platformFilter] = useState('all');
  const [dateRange, setDateRange] = useState('last_7_days');
  const [inactiveCounts, setInactiveCounts] = useState({ meta_inactive_count: 0, google_inactive_count: 0 });
  const [attentionPlatformFilter, setAttentionPlatformFilter] = useState('all'); // 'all', 'meta', 'google'
  const [pendingKey, setPendingKey] = useState(null); // keyboard shortcut chord state
  const [perfComparison, setPerfComparison] = useState(null);
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const [liveLastRefreshed, setLiveLastRefreshed] = useState(new Date());
  const [credentialStatus, setCredentialStatus] = useState(null);

  // Poll API credential health every 5 minutes
  useEffect(() => {
    const checkCredentials = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem('signal_auth') || '{}');
        if (!auth.token) return;
        const res = await fetch(`${API_URL}/credentials/status`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (res.ok) setCredentialStatus(await res.json());
      } catch {}
    };
    checkCredentials();
    const interval = setInterval(checkCredentials, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Derived: total inactive for health bar (component-level so accessible in all JSX scopes)
  // eslint-disable-next-line no-unused-vars
  const totalInactive = (inactiveCounts?.meta_inactive_count || 0) + (inactiveCounts?.google_inactive_count || 0);

  const addToast = useCallback((messageOrObj, type = 'info') => {
    const id = Date.now();
    // Handle both object format {type, message} and tuple format (message, type)
    let finalMessage = messageOrObj;
    let finalType = type;
    
    if (typeof messageOrObj === 'object' && messageOrObj !== null) {
      // Object format: {type, message}
      finalMessage = messageOrObj.message || JSON.stringify(messageOrObj);
      finalType = messageOrObj.type || type;
    }
    
    setToasts(prev => [...prev, { id, message: finalMessage, type: finalType }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Fetch health overview data - accurate health scores
  const fetchHealthOverview = useCallback(async (range) => {
    try {
      const dateRangeToUse = range || dateRange;
      const response = await fetch(`${API_URL}/health-overview?date_range=${dateRangeToUse}`);
      if (response.ok) {
        const result = await response.json();
        setHealthOverview(result);
      }
    } catch (error) {
      // health overview is supplementary; main data still loads
    }
  }, [dateRange]);

  // Fetch Meta account balance data
  const fetchMetaBalances = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/meta/balances`);
      if (response.ok) {
        const result = await response.json();
        setMetaBalances(result);
      } else {
        let msg = `Server error (${response.status})`;
        try { const e = await response.json(); msg = e.detail || msg; } catch {}
        setMetaBalances({ api_error: msg, summary: {}, recharge_needed: [], accounts: [], recharge_threshold_days: 4 });
      }
    } catch (error) {
      setMetaBalances({ api_error: 'Could not reach the server. Check your connection.', summary: {}, recharge_needed: [], accounts: [], recharge_threshold_days: 4 });
    }
  }, []);

  // Fetch Google budget concerns
  const fetchGoogleBudgetConcerns = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/google/budget-concerns`);
      if (response.ok) {
        const result = await response.json();
        setGoogleBudgetConcerns(result);
      } else {
        setGoogleBudgetConcerns({ summary: { accounts_with_concerns: 0 }, concerns: [], healthy: [] });
      }
    } catch (error) {
      setGoogleBudgetConcerns({ summary: { accounts_with_concerns: 0 }, concerns: [], healthy: [] });
    }
  }, []);

  // Fetch anomalies — accounts spending with zero conversions
  const fetchAnomalies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/anomalies`);
      if (response.ok) {
        const result = await response.json();
        setAnomalies(result);
      }
    } catch (error) {
      // non-critical
    }
  }, []);

  // Fetch period-over-period performance comparison for delta badges
  const fetchPerformanceComparison = useCallback(async (range) => {
    const dateRangeToUse = range || dateRange;
    try {
      const response = await fetch(`${API_URL}/performance-comparison?platform=all&date_range=${dateRangeToUse}`);
      if (response.ok) {
        const result = await response.json();
        setPerfComparison(result);
      }
    } catch (error) {
      // non-critical — delta badges simply won't render
    }
  }, [dateRange]);

  // Fetch sync status for data freshness indicator
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/sync/status`);
      if (response.ok) {
        const result = await response.json();
        setSyncStatus(result);
      }
    } catch (error) {
      // non-critical, no toast needed
    }
  }, []);

  // Fetch inactive accounts counts for badges - TIMEFRAME DEPENDENT
  const fetchInactiveCounts = useCallback(async (range) => {
    const dateRangeToUse = range || dateRange;
    try {
      const response = await fetch(`${API_URL}/inactive-accounts/counts?date_range=${dateRangeToUse}`);
      if (response.ok) {
        const result = await response.json();
        setInactiveCounts(result);
      }
    } catch (error) {
      // badge counts are supplementary
    }
  }, [dateRange]);

  // Fetch data from historical cache (instant!)
  const fetchData = useCallback(async (range) => {
    const dateRangeToUse = range || dateRange;
    try {
      const response = await fetch(`${API_URL}/dashboard/fast?date_range=${dateRangeToUse}`);
      const result = await response.json();
      
      if (result.status === "no_data") {
        const fallbackResponse = await fetch(`${API_URL}/dashboard`);
        const fallbackResult = await fallbackResponse.json();
        setData(fallbackResult);
      } else {
        setData(result);
      }
    } catch (error) {
      // Fall back to regular endpoint
      try {
        const fallbackResponse = await fetch(`${API_URL}/dashboard`);
        const fallbackResult = await fallbackResponse.json();
        setData(fallbackResult);
      } catch (e) {
        addToast('Failed to load dashboard data', 'error');
      }
    }
    setLoading(false);
  }, [addToast, dateRange]);

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/team`);
      if (response.ok) {
        const result = await response.json();
        setTeamMembers(result.members || []);
      }
    } catch (error) {
      addToast('Failed to load team members', 'error');
    }
  };

  useEffect(() => {
    fetchData();
    fetchTeamMembers();
    fetchInactiveCounts();
    fetchHealthOverview();
    fetchGoogleBudgetConcerns();
    fetchSyncStatus();
    fetchAnomalies();
    fetchPerformanceComparison();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, fetchInactiveCounts, fetchHealthOverview, fetchGoogleBudgetConcerns, fetchSyncStatus, fetchAnomalies, fetchPerformanceComparison]);

  // /api/meta/balances issues ~4 Graph calls per account, so it costs tens of
  // seconds on a large portfolio (~43s at 199 accounts). Firing it alongside the
  // critical-path fetches holds one of the browser's 6 connections per origin for
  // that whole time, and everything queued behind it stalls. It only feeds the
  // recharge panels, so defer it until the browser is idle and the first paint
  // has landed.
  useEffect(() => {
    let idleId, timeoutId;
    if (window.requestIdleCallback) {
      idleId = window.requestIdleCallback(() => fetchMetaBalances(), { timeout: 3000 });
    } else {
      timeoutId = setTimeout(fetchMetaBalances, 1000);
    }
    return () => {
      if (idleId !== undefined && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [fetchMetaBalances]);

  // Keyboard shortcut chord system (g→h/a/m/g, /, r)
  useEffect(() => {
    let chordTimer = null;

    const handleKeyDown = (e) => {
      // ⌘K / Ctrl+K → Command Palette (works even in inputs)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen(open => !open);
        return;
      }

      // Ignore when focused in an input or contenteditable
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;

      const key = e.key;

      if (pendingKey === 'g') {
        clearTimeout(chordTimer);
        setPendingKey(null);
        if (key === 'h') { setActiveTab('overview'); return; }
        if (key === 'a') { setActiveTab('alerts'); return; }
        if (key === 'm') { setActiveTab('meta'); return; }
        if (key === 'g') { setActiveTab('google'); return; }
        // unrecognised second key — fall through
        return;
      }

      if (key === 'g') {
        setPendingKey('g');
        chordTimer = setTimeout(() => setPendingKey(null), 1500);
        return;
      }

      if (key === 'r') {
        fetchData();
        return;
      }

      if (key === '/') {
        e.preventDefault();
        // Focus first visible search input if present
        const searchInput = document.querySelector('input[type="search"], input[placeholder*="earch"]');
        if (searchInput) searchInput.focus();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(chordTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, fetchData]);

  // Live auto-refresh: silently re-fetch inactive counts + sync status every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await fetchInactiveCounts(dateRange);
        await fetchSyncStatus();
        setLiveLastRefreshed(new Date());
      } catch (_) {}
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Change date range - re-fetches all date-sensitive data
  const handleDateRangeChange = async (newRange) => {
    setDateRange(newRange);
    setLoading(true);
    await fetchData(newRange);
    fetchInactiveCounts(newRange);
    fetchPerformanceComparison(newRange);
    fetchHealthOverview(newRange);
    addToast(`Showing data for ${DATE_RANGES.find(r => r.id === newRange)?.label || newRange}`, 'success');
  };

  // Full sync with ad platforms (updates historical cache)
  const handleSync = async () => {
    setSyncing(true);
    addToast('Starting fast sync with ad platforms...', 'info');
    try {
      // Trigger FAST parallel sync (5-10x faster)
      await fetch(`${API_URL}/historical/sync/fast?days=3&batch_size=15`, { method: 'POST' });
      // Also trigger inactive accounts sync
      await fetch(`${API_URL}/inactive-accounts/sync`, { method: 'POST' });
      // Wait for initial data to sync (faster with parallel)
      await new Promise(resolve => setTimeout(resolve, 5000));
      await fetchData();
      await fetchInactiveCounts();
      addToast('Data syncing! Dashboard will update shortly.', 'success');
    } catch (error) {
      addToast('Data sync failed.', 'error');
    }
    setSyncing(false);
  };

  // Trigger immediate sync via new /api/sync/now endpoint
  const handleSyncNow = async () => {
    setSyncingNow(true);
    try {
      const response = await fetch(`${API_URL}/sync/now`, { method: 'POST' });
      if (response.ok) {
        addToast('Sync started!', 'success');
        await fetchSyncStatus();
      } else {
        addToast('Sync failed', 'error');
      }
    } catch (error) {
      addToast('Sync failed', 'error');
    }
    setSyncingNow(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleRunAIAnalysis = async (accountId) => {
    addToast('Running AI analysis...', 'info');
    try {
      const response = await fetch(`${API_URL}/accounts/${accountId}/analyze`, { method: 'POST' });
      const result = await response.json();
      addToast(`Analysis complete! Score: ${Math.round(result.overall_health_score || 0)}%`, 'success');
      await fetchData();
    } catch (error) {
      addToast('AI analysis failed', 'error');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSaveSettings = async (id, settings) => {
    try {
      await fetch(`${API_URL}/accounts/${id}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      fetchData();
    } catch (error) {
      addToast('Failed to save settings', 'error');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSaveAssignment = async (id, assignment) => {
    await fetch(`${API_URL}/accounts/${id}/assignment`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(assignment)
    });
    fetchData();
    addToast('Assignment updated', 'success');
  };

  // eslint-disable-next-line no-unused-vars
  const handleExpandAccount = (accountId) => {
    setExpandedAccountId(expandedAccountId === accountId ? null : accountId);
  };

  // Filter accounts
  // eslint-disable-next-line no-unused-vars
  const filteredAccounts = data?.accounts?.filter(account => {
    const matchesSearch = account.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = platformFilter === 'all' || account.platform === platformFilter;
    return matchesSearch && matchesPlatform;
  }) || [];

  // Loading State
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#02040A]">
      <div className="text-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-[#22c55e]/20 rounded-full animate-spin border-b-[#22c55e]" style={{ animationDirection: 'reverse' }}></div>
          </div>
        </div>
        <p className="text-zinc-500 mt-4 text-sm">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#02040A] text-white flex">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(99,102,241,0.08)_0%,transparent_50%),radial-gradient(circle_at_90%_80%,rgba(34,197,94,0.05)_0%,transparent_50%)] pointer-events-none" />
      
      {/* Toasts */}
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
      ))}

      {/* Sidebar - Desktop */}
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} data={data} inactiveCounts={inactiveCounts} healthOverview={healthOverview} metaBalances={metaBalances} />

      {/* Main Content */}
      <main className="flex-1 min-w-0 pb-20 lg:pb-0">
        {/* Top Header */}
        <header className="sticky top-0 z-30 glass border-b border-white/5">
          <div className="px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between gap-4">
              {/* Mobile Logo */}
              <div className="lg:hidden">
                <img src="/logo.png" alt="Signal by Profitcast" className="h-10 w-auto" onError={(e) => e.target.style.display='none'} />
              </div>

              {/* Data freshness indicator + Live badge */}
              <div className="hidden lg:flex flex-1 items-center gap-3">
                {/* ● Live indicator */}
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-full cursor-default"
                  title={`Live data · Last refreshed ${liveLastRefreshed.toLocaleTimeString()}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
                  <span className="text-[10px] font-bold text-[#22c55e] tracking-wide">LIVE</span>
                </div>
                {syncStatus?.last_sync_at ? (
                  <span className="font-mono text-xs text-zinc-500">
                    {syncStatus.is_syncing
                      ? 'Syncing...'
                      : (() => { const mins = Math.round((Date.now() - new Date(syncStatus.last_sync_at).getTime()) / 60000); return mins < 60 ? `Synced ${mins}m ago` : mins < 1440 ? `Synced ${Math.round(mins/60)}h ago` : `Synced ${Math.round(mins/1440)}d ago`; })()}
                  </span>
                ) : (
                  <span className="font-mono text-xs text-zinc-600">No sync data</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {/* Sync Now button */}
                <button
                  onClick={handleSyncNow}
                  disabled={syncingNow || syncStatus?.is_syncing}
                  className={`px-3 py-2 rounded-xl font-semibold text-sm flex items-center gap-2 border ${
                    syncingNow || syncStatus?.is_syncing
                      ? 'border-zinc-700 text-zinc-500 cursor-not-allowed bg-zinc-800/50'
                      : 'border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10'
                  }`}
                  title="Sync Now"
                >
                  <svg className={`w-4 h-4 ${syncingNow || syncStatus?.is_syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">{syncingNow || syncStatus?.is_syncing ? 'Syncing...' : 'Sync Now'}</span>
                </button>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors ${
                    syncing
                      ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                      : 'bg-[#22c55e] text-black hover:bg-[#16a34a] shadow-lg shadow-[#22c55e]/20'
                  }`}
                >
                  <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Full Sync'}</span>
                </button>
                {/* ⌘K Command Palette button */}
                <button
                  onClick={() => setCmdPaletteOpen(true)}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl text-zinc-500 hover:text-white hover:bg-white/5 border border-white/5 hover:border-white/10 transition-colors text-xs font-mono"
                  title="Open Command Palette (⌘K)"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>⌘K</span>
                </button>
                <button
                  onClick={onLogout}
                  className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                  title="Sign Out"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="px-4 lg:px-8 py-6 space-y-6">

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Account Health Dashboard Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                    <svg className="w-7 h-7 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                    Account Health Dashboard
                  </h1>
                  <p className="text-zinc-500 text-sm mt-1">Monitor account status across all platforms</p>
                </div>
                <div className="flex items-center gap-2">
                  <DateRangeSelector selectedRange={dateRange} onRangeChange={handleDateRangeChange} />
                </div>
              </div>

              {/* Primary Metrics - Account Status Overview */}
              {(() => {
                const allAccounts = data?.accounts || [];
                const metaAccounts = allAccounts.filter(a => a.platform === 'META');
                const googleAccounts = allAccounts.filter(a => a.platform === 'GOOGLE');

                // Active = has spend in selected range
                const metaActive = metaAccounts.filter(a => a.metrics?.spend > 0);
                const googleActive = googleAccounts.filter(a => a.metrics?.spend > 0);

                // Fallback total counts from budget-concerns API (always queries all accounts in DB)
                const googleTotalCount = googleAccounts.length || googleBudgetConcerns?.summary?.total_google_accounts || 0;
                
                // Use healthOverview for accurate health counts (consistent with Performance tabs)
                // Health overview data is calculated in real-time and matches Performance dashboards
                const metaWarning = healthOverview?.warning?.filter(a => a.platform === 'META') || [];
                const googleWarning = healthOverview?.warning?.filter(a => a.platform === 'GOOGLE') || [];
                const metaCritical = healthOverview?.critical?.filter(a => a.platform === 'META') || [];
                const googleCritical = healthOverview?.critical?.filter(a => a.platform === 'GOOGLE') || [];
                
                // Need Attention = warning + critical accounts from health overview
                const metaNeedAttention = [...metaWarning, ...metaCritical];
                const googleNeedAttention = [...googleWarning, ...googleCritical];
                const totalNeedAttention = metaNeedAttention.length + googleNeedAttention.length;
                
                return (
                  <>
                    {/* Portfolio — searchable, sortable, grouped worst-first.
                        Replaced four summary cards that gave counts but no route to
                        the account behind them (see AccountsTable). */}
                    <AccountsTable
                      accounts={data?.accounts || []}
                      onSelectAccount={(a) => setActiveTab(a.platform === 'META' ? 'meta' : 'google')}
                    />

                    {/* Portfolio Performance — Spend / Leads / CPL with period-over-period delta badges */}
                    {(() => {
                      const totals = perfComparison?.totals;
                      const dateLabel = perfComparison?.date_range === 'last_14_days'
                        ? 'prev 14d' : perfComparison?.date_range === 'last_30_days'
                        ? 'prev 30d' : 'prev 7d';

                      const DeltaBadge = ({ pct, invertGood }) => {
                        if (pct === null || pct === undefined) return null;
                        const isGood = invertGood ? pct <= 0 : pct >= 0;
                        const absVal = Math.abs(pct).toFixed(1);
                        return (
                          <span className={`font-mono text-xs tracking-wide ${isGood ? 'text-[#22c55e]' : 'text-red-400'}`}>
                            {pct >= 0 ? '↑' : '↓'} {absVal}% vs {dateLabel}
                          </span>
                        );
                      };

                      const allAccounts = data?.accounts || [];
                      const curSpend = totals?.current_spend ?? allAccounts.reduce((s, a) => s + (a.metrics?.spend || 0), 0);
                      const curLeads = totals?.current_leads ?? allAccounts.reduce((s, a) => s + (a.metrics?.leads || 0) + (a.metrics?.messaging_leads || 0), 0);
                      const curCpl = (totals?.current_cpl !== undefined && totals?.current_cpl !== null)
                        ? totals.current_cpl
                        : (curLeads > 0 ? Math.round(curSpend / curLeads) : null);

                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {/* Total Spend */}
                          <div className="bg-[#09090B] border border-white/5 rounded-xl p-5 flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Total Spend</span>
                              <svg className="w-4 h-4 text-[#22c55e] opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <p className="text-2xl font-bold text-white font-mono tracking-tight">
                              ₹{curSpend >= 1000 ? `${(curSpend / 1000).toFixed(1)}K` : Math.round(curSpend).toLocaleString()}
                            </p>
                            <DeltaBadge pct={totals?.spend_change_pct} invertGood={false} />
                          </div>

                          {/* Total Leads */}
                          <div className="bg-[#09090B] border border-white/5 rounded-xl p-5 flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Total Leads</span>
                              <svg className="w-4 h-4 text-[#22c55e] opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <p className="text-2xl font-bold text-white font-mono tracking-tight">
                              {curLeads >= 1000 ? `${(curLeads / 1000).toFixed(1)}K` : curLeads.toLocaleString()}
                            </p>
                            <DeltaBadge pct={totals?.leads_change_pct} invertGood={false} />
                          </div>

                          {/* Avg CPL */}
                          <div className="bg-[#09090B] border border-white/5 rounded-xl p-5 flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Avg CPL</span>
                              <svg className="w-4 h-4 text-[#22c55e] opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                            <p className="text-2xl font-bold text-white font-mono tracking-tight">
                              {curCpl !== null ? `₹${Math.round(curCpl).toLocaleString()}` : '—'}
                            </p>
                            {/* For CPL: lower is better, so invert the green/red logic */}
                            <DeltaBadge pct={totals?.cpl_change_pct} invertGood={true} />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Platform Quick Access Cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Meta Ads Quick Access */}
                      <div className="bg-[#09090B] rounded-2xl border border-white/10 p-6 hover:border-[#1877F2]/30 transition-colors cursor-pointer group"
                           onClick={() => setActiveTab('meta')}>
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-[#1877F2] rounded-xl flex items-center justify-center shadow-lg shadow-[#1877F2]/30">
                              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-white group-hover:text-[#1877F2] transition-colors">Meta Ads</h3>
                              <p className="text-sm text-zinc-500">{metaAccounts.length} total accounts</p>
                            </div>
                          </div>
                          <svg className="w-6 h-6 text-zinc-600 group-hover:text-[#1877F2] group-hover:translate-x-1 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-[#22c55e]/10 rounded-xl p-4 text-center border border-[#22c55e]/20 hover:border-[#22c55e]/50 cursor-pointer transition-colors"
                               onClick={(e) => { e.stopPropagation(); setActiveTab('meta'); }}>
                            <p className="text-2xl font-bold text-[#22c55e] font-mono">{metaActive.length}</p>
                            <p className="text-[10px] text-zinc-500 uppercase mt-1">Active</p>
                          </div>
                          <div className="bg-yellow-500/10 rounded-xl p-4 text-center border border-yellow-500/20 hover:border-yellow-500/50 cursor-pointer transition-colors"
                               onClick={(e) => { e.stopPropagation(); setActiveTab('meta'); }}>
                            <p className="text-2xl font-bold text-yellow-400 font-mono">{inactiveCounts?.meta_inactive_count || 0}</p>
                            <p className="text-[10px] text-zinc-500 uppercase mt-1">Inactive</p>
                          </div>
                          <div className="bg-red-500/10 rounded-xl p-4 text-center border border-red-500/20 hover:border-red-500/50 cursor-pointer transition-colors"
                               onClick={(e) => { 
                                 e.stopPropagation(); 
                                 setAttentionPlatformFilter('meta');
                                 document.getElementById('accounts-needing-attention')?.scrollIntoView({ behavior: 'smooth' });
                               }}>
                            <p className="text-2xl font-bold text-red-400 font-mono">{metaNeedAttention.length}</p>
                            <p className="text-[10px] text-zinc-500 uppercase mt-1">Attention</p>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500">
                          <span className="px-2 py-1 bg-white/5 rounded">View detailed performance</span>
                          <span className="px-2 py-1 bg-white/5 rounded">Campaign analytics</span>
                        </div>
                      </div>

                      {/* Google Ads Quick Access */}
                      <div className="bg-[#09090B] rounded-2xl border border-white/10 p-6 hover:border-[#4285F4]/30 transition-colors cursor-pointer group"
                           onClick={() => setActiveTab('google')}>
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-white/10">
                              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                                <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
                                <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
                                <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
                                <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-white group-hover:text-[#4285F4] transition-colors">Google Ads</h3>
                              <p className="text-sm text-zinc-500">{googleTotalCount} total accounts</p>
                            </div>
                          </div>
                          <svg className="w-6 h-6 text-zinc-600 group-hover:text-[#4285F4] group-hover:translate-x-1 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-[#22c55e]/10 rounded-xl p-4 text-center border border-[#22c55e]/20 hover:border-[#22c55e]/50 cursor-pointer transition-colors"
                               onClick={(e) => { e.stopPropagation(); setActiveTab('google'); }}>
                            <p className="text-2xl font-bold text-[#22c55e] font-mono">{googleActive.length}</p>
                            <p className="text-[10px] text-zinc-500 uppercase mt-1">Active</p>
                          </div>
                          <div className="bg-yellow-500/10 rounded-xl p-4 text-center border border-yellow-500/20 hover:border-yellow-500/50 cursor-pointer transition-colors"
                               onClick={(e) => { e.stopPropagation(); setActiveTab('google'); }}>
                            <p className="text-2xl font-bold text-yellow-400 font-mono">{inactiveCounts?.google_inactive_count || 0}</p>
                            <p className="text-[10px] text-zinc-500 uppercase mt-1">Inactive</p>
                          </div>
                          <div className="bg-red-500/10 rounded-xl p-4 text-center border border-red-500/20 hover:border-red-500/50 cursor-pointer transition-colors"
                               onClick={(e) => { 
                                 e.stopPropagation(); 
                                 setAttentionPlatformFilter('google');
                                 document.getElementById('accounts-needing-attention')?.scrollIntoView({ behavior: 'smooth' });
                               }}>
                            <p className="text-2xl font-bold text-red-400 font-mono">{googleNeedAttention.length}</p>
                            <p className="text-[10px] text-zinc-500 uppercase mt-1">Attention</p>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500">
                          <span className="px-2 py-1 bg-white/5 rounded">View detailed performance</span>
                          <span className="px-2 py-1 bg-white/5 rounded">Campaign analytics</span>
                        </div>
                      </div>
                    </div>

                    {/* Meta Account Balances Card */}
                    {metaBalances && (
                      <div className="bg-[#09090B] rounded-2xl border border-[#1877F2]/20 p-6 hover:border-[#1877F2]/40 transition-colors">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#1877F2] to-[#22c55e] rounded-xl flex items-center justify-center shadow-lg">
                              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-white">Accounts Needing Recharge</h3>
                              <p className="text-sm text-zinc-500">
                                {metaBalances.summary?.recharge_needed_count || 0} Meta accounts with &lt;{metaBalances.recharge_threshold_days ?? 4} days budget
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {metaBalances.api_error && (
                              <span className="text-xs text-yellow-400 bg-yellow-500/10 px-3 py-1 rounded-full">
                                {metaBalances.api_error.includes('expired') ? 'Token Expired' : 'API Error'}
                              </span>
                            )}
                            {!metaBalances.api_error && metaBalances.summary?.recharge_needed_count > 0 && (
                              <button
                                onClick={() => setActiveTab('recharge')}
                                className="text-xs text-[#1877F2] bg-[#1877F2]/10 px-3 py-1.5 rounded-full hover:bg-[#1877F2]/20 transition-colors font-medium flex items-center gap-1"
                              >
                                Show All
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        {metaBalances.api_error ? (
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
                            <svg className="w-8 h-8 text-yellow-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <p className="text-sm text-yellow-400 font-medium">{metaBalances.api_error.includes('expired') ? 'Meta API Token Expired' : 'Meta API Error'}</p>
                            <p className="text-xs text-zinc-500 mt-1">{metaBalances.api_error}</p>
                          </div>
                        ) : (
                          <>
                            {/* Summary Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                              <div className="bg-[#22c55e]/10 rounded-xl p-4 border border-[#22c55e]/20">
                                <p className="text-2xl font-bold text-[#22c55e] font-mono">
                                  {formatCurrency(metaBalances.summary?.total_available_balance || 0)}
                                </p>
                                <p className="text-xs text-zinc-500 mt-1">Total Balance</p>
                              </div>
                              <div className="bg-[#6366f1]/10 rounded-xl p-4 border border-[#6366f1]/20">
                                <p className="text-2xl font-bold text-[#6366f1] font-mono">
                                  {metaBalances.summary?.healthy_count || 0}
                                </p>
                                <p className="text-xs text-zinc-500 mt-1">Healthy (&gt;4 days)</p>
                              </div>
                              <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/20">
                                <p className="text-2xl font-bold text-yellow-400 font-mono">
                                  {(metaBalances.summary?.warning_count || 0) + (metaBalances.summary?.urgent_count || 0)}
                                </p>
                                <p className="text-xs text-zinc-500 mt-1">2-4 Days Left</p>
                              </div>
                              <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                                <p className="text-2xl font-bold text-red-400 font-mono">
                                  {metaBalances.summary?.critical_count || 0}
                                </p>
                                <p className="text-xs text-zinc-500 mt-1">&lt;1 Day Left</p>
                              </div>
                            </div>

                            {/* Accounts Needing Recharge List */}
                            {metaBalances.recharge_needed && metaBalances.recharge_needed.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-zinc-400 flex items-center gap-2">
                                  <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> Accounts Running Low on Budget
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[200px] overflow-y-auto custom-scrollbar">
                                  {metaBalances.recharge_needed.slice(0, 6).map(account => (
                                    <div 
                                      key={account.account_id}
                                      className={`p-3 rounded-xl border transition-colors ${
                                        account.recharge_status === 'critical'
                                          ? 'bg-red-500/5 border-red-500/30'
                                          : account.recharge_status === 'urgent'
                                            ? 'bg-orange-500/5 border-orange-500/30'
                                            : 'bg-yellow-500/5 border-yellow-500/30'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-white truncate max-w-[150px]">
                                          {account.account_name}
                                        </p>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                          account.recharge_status === 'critical'
                                            ? 'bg-red-500/20 text-red-400'
                                            : account.recharge_status === 'urgent'
                                              ? 'bg-orange-500/20 text-orange-400'
                                              : 'bg-yellow-500/20 text-yellow-400'
                                        }`}>
                                          {account.days_remaining !== null ? `${account.days_remaining}d left` : account.recharge_status}
                                        </span>
                                      </div>
                                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <span className="text-zinc-500">Balance</span>
                                          <p className={`font-mono font-bold ${
                                            account.recharge_status === 'critical' ? 'text-red-400' :
                                            account.recharge_status === 'urgent' ? 'text-orange-400' : 'text-yellow-400'
                                          }`}>
                                            {formatCurrency(account.balance || 0)}
                                          </p>
                                        </div>
                                        <div>
                                          <span className="text-zinc-500">Daily Budget</span>
                                          <p className="font-mono text-zinc-300">
                                            {formatCurrency(account.total_daily_budget || 0)}
                                          </p>
                                        </div>
                                      </div>
                                      {account.active_campaign_count > 0 && (
                                        <div className="mt-2 text-[10px] text-zinc-500">
                                          {account.active_campaign_count} active campaign{account.active_campaign_count > 1 ? 's' : ''}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {metaBalances.recharge_needed.length > 6 && (
                                  <button
                                    onClick={() => setActiveTab('recharge')}
                                    className="w-full text-sm text-[#1877F2] hover:text-[#1877F2]/80 bg-[#1877F2]/5 hover:bg-[#1877F2]/10 py-2 rounded-xl transition-colors font-medium"
                                  >
                                    +{metaBalances.recharge_needed.length - 6} more accounts need recharge → View All
                                  </button>
                                )}
                              </div>
                            )}

                            {/* No recharge needed message */}
                            {(!metaBalances.recharge_needed || metaBalances.recharge_needed.length === 0) && metaBalances.accounts?.length > 0 && (
                              <div className="bg-[#22c55e]/5 border border-[#22c55e]/20 rounded-xl p-4 text-center">
                                <svg className="w-8 h-8 text-[#22c55e] mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-[#22c55e] font-medium">All accounts have 4+ days of budget</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Accounts Needing Attention List */}
                    {totalNeedAttention > 0 && (
                      <div id="accounts-needing-attention" className="bg-[#09090B] rounded-2xl border border-red-500/20 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <span className="text-xl">⚠️</span> Accounts Needing Attention
                          </h3>
                          <span className="text-xs text-red-400 bg-red-500/10 px-3 py-1 rounded-full font-semibold">
                            {attentionPlatformFilter === 'meta' ? metaNeedAttention.length : 
                             attentionPlatformFilter === 'google' ? googleNeedAttention.length : 
                             totalNeedAttention} accounts
                          </span>
                        </div>
                        
                        {/* Platform Toggle Tabs */}
                        <div className="flex items-center gap-2 mb-4 bg-white/5 p-1 rounded-xl w-fit">
                          <button
                            onClick={() => setAttentionPlatformFilter('all')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              attentionPlatformFilter === 'all'
                                ? 'bg-white/10 text-white'
                                : 'text-zinc-500 hover:text-white'
                            }`}
                          >
                            All ({totalNeedAttention})
                          </button>
                          <button
                            onClick={() => setAttentionPlatformFilter('meta')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                              attentionPlatformFilter === 'meta'
                                ? 'bg-[#1877F2]/20 text-[#1877F2]'
                                : 'text-zinc-500 hover:text-[#1877F2]'
                            }`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            Meta ({metaNeedAttention.length})
                          </button>
                          <button
                            onClick={() => setAttentionPlatformFilter('google')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                              attentionPlatformFilter === 'google'
                                ? 'bg-[#4285F4]/20 text-[#4285F4]'
                                : 'text-zinc-500 hover:text-[#4285F4]'
                            }`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                              <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="currentColor"/>
                              <path d="M12.24 24.0008C15.4764 24.0008 18.2058 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.2444 19.252C9.11376 19.252 6.45934 17.1399 5.50693 14.3003H1.51648V17.3912C3.55359 21.4434 7.70278 24.0008 12.24 24.0008Z" fill="currentColor"/>
                              <path d="M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61487H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="currentColor"/>
                              <path d="M12.24 4.74966C13.9508 4.7232 15.6043 5.36697 16.8433 6.54867L20.2694 3.12262C18.1 1.0855 15.2207 -0.034466 12.24 0.000808666C7.70277 0.000808666 3.55359 2.55822 1.51648 6.61481L5.50252 9.7057C6.45052 6.86173 9.10935 4.74966 12.24 4.74966Z" fill="currentColor"/>
                            </svg>
                            Google ({googleNeedAttention.length})
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[480px] overflow-y-auto custom-scrollbar">
                          {(attentionPlatformFilter === 'meta' ? metaNeedAttention :
                            attentionPlatformFilter === 'google' ? googleNeedAttention :
                            [...metaNeedAttention, ...googleNeedAttention]
                          // Sort: critical first, then by health_score ascending (worst first)
                          ).sort((a, b) => a.health_score - b.health_score).map(account => {
                            const healthStatus = account.health_score < 40 ? 'critical' : 'warning';
                            const hasCPR = account.cpr_7d != null;
                            const hasSpend = (account.spend_7d || 0) > 0;
                            return (
                            <div
                              key={account.id}
                              className={`p-4 rounded-xl border transition-colors hover:scale-[1.01] cursor-pointer ${
                                healthStatus === 'critical'
                                  ? 'bg-red-500/5 border-red-500/30 hover:border-red-500/50'
                                  : 'bg-yellow-500/5 border-yellow-500/30 hover:border-yellow-500/50'
                              }`}
                              onClick={() => setActiveTab(account.platform === 'META' ? 'meta' : 'google')}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <PlatformIcon platform={account.platform} size="sm" />
                                  <div>
                                    <p className="text-sm font-semibold text-white truncate max-w-[150px]">{account.name}</p>
                                    <p className="text-xs text-zinc-500">{account.campaign_count || 0} campaigns</p>
                                  </div>
                                </div>
                                <HealthBadge status={healthStatus} score={account.health_score} />
                              </div>
                              <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-zinc-500">7d Spend</span>
                                  <p className="font-mono text-zinc-300 font-semibold">{hasSpend ? formatCurrency(account.spend_7d) : '—'}</p>
                                </div>
                                <div>
                                  <span className="text-zinc-500">{hasCPR ? 'CPR' : account.clicks_7d > 0 ? 'Clicks' : 'Results'}</span>
                                  <p className={`font-mono font-semibold ${healthStatus === 'critical' ? 'text-red-400' : 'text-yellow-400'}`}>
                                    {hasCPR ? formatCurrency(account.cpr_7d) : account.clicks_7d > 0 ? account.clicks_7d.toLocaleString() : (account.results_7d || 0)}
                                  </p>
                                </div>
                              </div>
                              {account.critical_campaigns > 0 && (
                                <div className="mt-2 flex items-center gap-1 text-xs text-red-400">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" /></svg>
                                  {account.critical_campaigns} critical campaign{account.critical_campaigns !== 1 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          )})}
                        </div>
                      </div>
                    )}

                    {/* Health Status Summary Bar */}
                    <div className="bg-[#09090B] rounded-2xl border border-white/10 p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" /></svg> Account Health Distribution
                      </h3>
                      <div className="space-y-4">
                        {/* Health Bar Visualization - using healthOverview for consistency */}
                        {(() => {
                          const healthyCount = healthOverview?.summary?.healthy_count || 0;
                          const warningCount = healthOverview?.summary?.warning_count || 0;
                          const criticalCount = healthOverview?.summary?.critical_count || 0;
                          const totalHealthAccounts = healthyCount + warningCount + criticalCount;
                          const inactiveCount = (inactiveCounts?.meta_inactive_count || 0) + (inactiveCounts?.google_inactive_count || 0);

                          return (
                            <>
                              <div className="h-4 rounded-full overflow-hidden bg-white/5 flex">
                                {healthyCount > 0 && (
                                  <div
                                    className="bg-[#22c55e] h-full"
                                    style={{ width: `${(healthyCount / (totalHealthAccounts + inactiveCount)) * 100}%` }}
                                    title={`${healthyCount} healthy accounts`}
                                  />
                                )}
                                {warningCount > 0 && (
                                  <div
                                    className="bg-yellow-400 h-full"
                                    style={{ width: `${(warningCount / (totalHealthAccounts + inactiveCount)) * 100}%` }}
                                    title={`${warningCount} warning accounts`}
                                  />
                                )}
                                {criticalCount > 0 && (
                                  <div
                                    className="bg-red-400 h-full"
                                    style={{ width: `${(criticalCount / (totalHealthAccounts + inactiveCount)) * 100}%` }}
                                    title={`${criticalCount} critical accounts`}
                                  />
                                )}
                                {inactiveCount > 0 && (
                                  <div
                                    className="bg-zinc-600 h-full"
                                    style={{ width: `${(inactiveCount / (totalHealthAccounts + inactiveCount)) * 100}%` }}
                                    title={`${inactiveCount} inactive accounts`}
                                  />
                                )}
                              </div>

                              {/* Legend */}
                              <div className="flex items-center justify-center gap-6 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-[#22c55e]"></div>
                                  <span className="text-sm text-zinc-400">Healthy ({healthyCount})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                                  <span className="text-sm text-zinc-400">Warning ({warningCount})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                  <span className="text-sm text-zinc-400">Critical ({criticalCount})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-zinc-600"></div>
                                  <span className="text-sm text-zinc-400">Inactive ({inactiveCount})</span>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Budget Recharge Alert Banner */}
                    {(() => {
                      const lowBudgetAccounts = metaBalances?.recharge_needed?.filter(
                        a => a.recharge_status === 'critical'
                      ) || [];
                      return lowBudgetAccounts.length > 0 && (
                        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-5">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></div>
                            <h3 className="font-bold text-orange-400 text-sm">Budget Recharge Required</h3>
                            <button
                              onClick={() => setActiveTab('recharge')}
                              className="ml-auto text-xs font-mono text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full hover:bg-orange-500/20 transition-colors"
                            >
                              {lowBudgetAccounts.length} account{lowBudgetAccounts.length !== 1 ? 's' : ''}
                            </button>
                          </div>
                          <div className="space-y-2">
                            {lowBudgetAccounts.slice(0, 5).map((acct, i) => (
                              <div key={acct.account_id || i} className="flex items-center justify-between text-xs">
                                <span className="text-zinc-300">{acct.account_name}</span>
                                <span className="font-mono text-orange-400">₹{(acct.balance || 0).toFixed(0)} left</span>
                              </div>
                            ))}
                          </div>
                          {lowBudgetAccounts.length > 5 && (
                            <button
                              onClick={() => setActiveTab('recharge')}
                              className="mt-3 text-xs text-orange-400 hover:text-orange-300 transition-colors"
                            >
                              +{lowBudgetAccounts.length - 5} more → View All
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Anomalies Alert Panel */}
                    {anomalies && anomalies.total_anomalies > 0 && (
                      <div className="bg-[#09090B] rounded-2xl border border-red-500/30 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                            Critical Anomalies Detected
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-mono">{anomalies.total_anomalies}</span>
                          </h3>
                          <button
                            onClick={fetchAnomalies}
                            className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh
                          </button>
                        </div>
                        <p className="text-sm text-zinc-400 mb-4">{anomalies.accounts_affected} account{anomalies.accounts_affected !== 1 ? 's' : ''} spending budget with zero results — immediate attention required</p>
                        <div className="space-y-3">
                          {anomalies.anomalies.map((account) => (
                            <div key={account.account_id} className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`text-xs px-2 py-0.5 rounded font-mono font-semibold ${account.platform === 'META' ? 'bg-[#1877F2]/20 text-[#1877F2]' : 'bg-[#4285F4]/20 text-[#4285F4]'}`}>
                                    {account.platform}
                                  </span>
                                  <span className="text-sm font-semibold text-white truncate">{account.account_name}</span>
                                </div>
                              </div>
                              <div className="mt-2 space-y-1">
                                {account.anomalies.map((a, idx) => (
                                  <div key={idx} className="flex items-start gap-2 text-xs">
                                    <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'}`}></span>
                                    <span className="text-zinc-300">{a.message}</span>
                                  </div>
                                ))}
                                {account.campaign_anomalies && account.campaign_anomalies.map((ca, idx) => (
                                  <div key={`ca-${idx}`} className="flex items-start gap-2 text-xs">
                                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-orange-500"></span>
                                    <span className="text-zinc-300">{ca.campaign_name}: {ca.anomalies?.[0]?.message || 'Campaign anomaly detected'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <button
                        onClick={() => setActiveTab('meta')}
                        className="bg-[#09090B] rounded-xl border border-white/10 p-4 hover:border-[#1877F2]/50 hover:bg-[#1877F2]/5 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-[#1877F2]/20 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                          </div>
                          <span className="text-sm font-semibold text-white group-hover:text-[#1877F2]">Meta Performance</span>
                        </div>
                        <p className="text-xs text-zinc-500">View detailed Meta analytics</p>
                      </button>

                      <button
                        onClick={() => setActiveTab('google')}
                        className="bg-[#09090B] rounded-xl border border-white/10 p-4 hover:border-[#4285F4]/50 hover:bg-[#4285F4]/5 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                              <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
                              <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
                              <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
                              <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
                            </svg>
                          </div>
                          <span className="text-sm font-semibold text-white group-hover:text-[#4285F4]">Google Performance</span>
                        </div>
                        <p className="text-xs text-zinc-500">View detailed Google analytics</p>
                      </button>

                      <button
                        onClick={() => setActiveTab('team')}
                        className="bg-[#09090B] rounded-xl border border-white/10 p-4 hover:border-purple-500/50 hover:bg-purple-500/5 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <span className="text-sm font-semibold text-white group-hover:text-purple-400">Team</span>
                        </div>
                        <p className="text-xs text-zinc-500">Manage team members</p>
                      </button>
                    </div>
                  </>
                );
              })()}

              {/* Empty State */}
              {(data?.accounts || []).length === 0 && (
                <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                    <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">No Accounts Found</h3>
                  <p className="text-zinc-500 mb-6">Sync your data to see account health metrics.</p>
                  <button
                    onClick={handleSync}
                    className="px-6 py-3 bg-[#22c55e] text-black rounded-xl text-sm font-bold hover:bg-[#16a34a] transition-colors shadow-lg shadow-[#22c55e]/20"
                  >
                    Sync Data Now
                  </button>
                </div>
              )}
            </div>
          )}

          {/* API Credential Warning Banner — shown on all tabs when a token is broken */}
          {credentialStatus && (!credentialStatus.meta?.valid || !credentialStatus.google?.valid) && activeTab !== 'api-credentials' && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 mb-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-3">
                <svg className="w-4 h-4 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <span className="text-sm text-red-300 font-['Inter']">
                  <span className="font-semibold">API token issue — </span>
                  {[!credentialStatus.meta?.valid && 'Meta Ads', !credentialStatus.google?.valid && 'Google Ads'].filter(Boolean).join(' & ')} token{(!credentialStatus.meta?.valid && !credentialStatus.google?.valid) ? 's are' : ' is'} expired or invalid.
                </span>
              </div>
              <button
                onClick={() => setActiveTab('api-credentials')}
                className="shrink-0 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-semibold rounded-lg transition-colors"
              >
                Fix Now →
              </button>
            </div>
          )}

          {activeTab === 'alerts' && <AlertsPanel addToast={addToast} />}
          {activeTab === 'health' && <HealthOverview addToast={addToast} />}
          {activeTab === 'meta' && <MetaPerformance addToast={addToast} />}
          {activeTab === 'google' && <GooglePerformance addToast={addToast} />}
          {activeTab === 'client-portal' && (
            <ClientPortalAdmin addToast={addToast} />
          )}
          {activeTab === 'team' && <TeamManagement teamMembers={teamMembers} onRefresh={fetchTeamMembers} addToast={addToast} />}
          {activeTab === 'sync-logs' && <SyncLogs addToast={addToast} />}
          {activeTab === 'telegram-reports' && <TelegramReports addToast={addToast} onNavigate={setActiveTab} />}
          {activeTab === 'api-credentials' && <ApiCredentials addToast={addToast} />}
          
          {/* Recharge Needed Page */}
          {activeTab === 'recharge' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                    <svg className="w-7 h-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    Accounts Needing Recharge
                  </h1>
                  <p className="text-sm text-zinc-500 mt-1">
                    Meta accounts with less than {metaBalances?.recharge_threshold_days ?? 4} days of budget • Google accounts with stopped/throttled spend
                  </p>
                </div>
                <button
                  onClick={fetchMetaBalances}
                  className="px-4 py-2 bg-[#1877F2]/10 text-[#1877F2] rounded-xl text-sm font-medium hover:bg-[#1877F2]/20 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              {/* Summary Cards */}
              {metaBalances && !metaBalances.api_error && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#09090B] rounded-2xl border border-white/10 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-orange-400 font-mono">
                      {metaBalances.summary?.recharge_needed_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">Total Need Recharge</p>
                  </div>
                  
                  <div className="bg-[#09090B] rounded-2xl border border-red-500/20 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
                        <span className="text-lg">🔴</span>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-red-400 font-mono">
                      {metaBalances.summary?.critical_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">&lt;1 Day Left</p>
                  </div>
                  
                  <div className="bg-[#09090B] rounded-2xl border border-orange-500/20 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
                        <span className="text-lg">🟠</span>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-orange-400 font-mono">
                      {metaBalances.summary?.urgent_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">1-2 Days Left</p>
                  </div>
                  
                  <div className="bg-[#09090B] rounded-2xl border border-yellow-500/20 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-yellow-500/10 rounded-xl flex items-center justify-center">
                        <span className="text-lg">🟡</span>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-yellow-400 font-mono">
                      {metaBalances.summary?.warning_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">2-4 Days Left</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {metaBalances?.api_error && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 text-center">
                  <svg className="w-12 h-12 text-yellow-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-lg text-yellow-400 font-medium">
                    {metaBalances.api_error.includes('expired') ? 'Meta API Token Expired' :
                     metaBalances.api_error.includes('reach') ? 'Cannot Reach Server' : 'Meta API Error'}
                  </p>
                  <p className="text-sm text-zinc-500 mt-1">{metaBalances.api_error}</p>
                  <button
                    onClick={fetchMetaBalances}
                    className="mt-4 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-xl text-sm font-medium hover:bg-yellow-500/30 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Loading State */}
              {!metaBalances && (
                <div className="bg-[#09090B] rounded-2xl border border-white/10 p-8 text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-[#22c55e] border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-zinc-400">Loading balance data…</p>
                  <p className="text-xs text-zinc-600 mt-1">Fetching live balances from Meta — may take a few seconds</p>
                </div>
              )}

              {/* Google Budget Concerns */}
              {googleBudgetConcerns && (
                <div className="bg-[#09090B] rounded-2xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-white">
                        Google Ads — {googleBudgetConcerns.summary?.accounts_with_concerns > 0
                          ? `${googleBudgetConcerns.summary.accounts_with_concerns} Budget Issue${googleBudgetConcerns.summary.accounts_with_concerns > 1 ? 's' : ''}`
                          : `${googleBudgetConcerns.summary?.total_google_accounts || 0} Accounts Monitored`}
                      </h3>
                    </div>
                    <button
                      onClick={fetchGoogleBudgetConcerns}
                      className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 bg-white/5 rounded-lg transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                  <p className="text-xs text-zinc-600 px-4 py-2 border-b border-white/5">{googleBudgetConcerns.note}</p>
                  {googleBudgetConcerns.concerns?.length > 0 ? (
                    <div className="divide-y divide-white/5">
                      {googleBudgetConcerns.concerns.map(account => (
                        <div key={account.account_id} className={`p-4 hover:bg-white/5 transition-colors ${account.status === 'stopped' ? 'bg-red-500/5' : 'bg-yellow-500/5'}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <p className="text-white font-medium">{account.account_name}</p>
                              <p className="text-xs text-zinc-500 mt-0.5">{account.message}</p>
                            </div>
                            <div className="flex items-center gap-4 text-right">
                              <div>
                                <p className="text-xs text-zinc-500">7d Spend</p>
                                <p className="font-mono font-bold text-sm text-zinc-300">₹{account.total_7d_spend?.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500">Avg/Day</p>
                                <p className="font-mono font-bold text-sm text-zinc-300">₹{account.daily_avg_spend?.toLocaleString()}</p>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full font-bold uppercase ${account.status === 'stopped' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                {account.status === 'stopped' ? `${account.days_inactive}d stopped` : 'throttled'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center">
                      <svg className="w-10 h-10 text-[#22c55e] mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-[#22c55e] font-medium">No budget concerns detected</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {googleBudgetConcerns.summary?.accounts_healthy > 0
                          ? `${googleBudgetConcerns.summary.accounts_healthy} healthy account${googleBudgetConcerns.summary.accounts_healthy > 1 ? 's' : ''} spending normally`
                          : `${googleBudgetConcerns.summary?.total_google_accounts || 0} accounts checked — no recent spend data`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Accounts List */}
              {metaBalances && !metaBalances.api_error && metaBalances.recharge_needed && (
                <div className="bg-[#09090B] rounded-2xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h3 className="text-lg font-semibold text-white">
                      All Accounts Needing Recharge ({metaBalances.recharge_needed.length})
                    </h3>
                  </div>
                  
                  {metaBalances.recharge_needed.length === 0 ? (
                    <div className="p-8 text-center">
                      <svg className="w-12 h-12 text-[#22c55e] mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-lg text-[#22c55e] font-medium">All accounts have sufficient budget!</p>
                      <p className="text-sm text-zinc-500 mt-1">No accounts need recharge at this time</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {metaBalances.recharge_needed.map(account => (
                        <div 
                          key={account.account_id}
                          className={`p-4 hover:bg-white/5 transition-colors ${
                            account.recharge_status === 'critical' ? 'bg-red-500/5' :
                            account.recharge_status === 'urgent' ? 'bg-orange-500/5' : 'bg-yellow-500/5'
                          }`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            {/* Account Info */}
                            <div className="flex items-center gap-4 flex-1">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                account.recharge_status === 'critical' ? 'bg-red-500/20' :
                                account.recharge_status === 'urgent' ? 'bg-orange-500/20' : 'bg-yellow-500/20'
                              }`}>
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                              </div>
                              <div>
                                <p className="text-white font-medium">{account.account_name}</p>
                                <p className="text-xs text-zinc-500">
                                  {account.active_campaign_count} active campaign{account.active_campaign_count !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-6 lg:gap-8">
                              <div className="text-center lg:text-right">
                                <p className="text-xs text-zinc-500 mb-1">Balance</p>
                                <p className={`text-lg font-bold font-mono ${
                                  account.recharge_status === 'critical' ? 'text-red-400' :
                                  account.recharge_status === 'urgent' ? 'text-orange-400' : 'text-yellow-400'
                                }`}>
                                  {formatCurrency(account.balance || 0)}
                                </p>
                              </div>
                              <div className="text-center lg:text-right">
                                <p className="text-xs text-zinc-500 mb-1">Daily Budget</p>
                                <p className="text-lg font-bold font-mono text-zinc-300">
                                  {formatCurrency(account.total_daily_budget || 0)}
                                </p>
                              </div>
                              <div className="text-center lg:text-right">
                                <p className="text-xs text-zinc-500 mb-1">Days Left</p>
                                <p className={`text-lg font-bold font-mono ${
                                  account.recharge_status === 'critical' ? 'text-red-400' :
                                  account.recharge_status === 'urgent' ? 'text-orange-400' : 'text-yellow-400'
                                }`}>
                                  {account.days_remaining !== null ? `${account.days_remaining}d` : '-'}
                                </p>
                              </div>
                            </div>
                            
                            {/* Status Badge */}
                            <div>
                              <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${
                                account.recharge_status === 'critical' ? 'bg-red-500/20 text-red-400' :
                                account.recharge_status === 'urgent' ? 'bg-orange-500/20 text-orange-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {account.recharge_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <MobileNav activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* ⌘K Command Palette */}
      <CommandPalette
        isOpen={cmdPaletteOpen}
        onClose={() => setCmdPaletteOpen(false)}
        accounts={(data?.accounts || []).map(a => ({
          id: a.id,
          name: a.name,
          platform: a.platform,
          campaign_count: a.campaigns?.length ?? 0,
        }))}
        onNavigate={(tab) => {
          setActiveTab(tab);
          setCmdPaletteOpen(false);
        }}
        onAction={async (action) => {
          setCmdPaletteOpen(false);
          if (action === 'sync') { await handleSyncNow(); }
          else if (action === 'export') {
            const link = document.createElement('a');
            link.href = `${API_URL}/export/accounts-csv`;
            link.download = 'signal-accounts.csv';
            link.click();
          }
          else if (action === 'generate-alerts') {
            addToast('Generating AI alerts…', 'info');
            try {
              await fetch(`${API_URL}/ai/generate-alerts?platform=META`, { method: 'POST' });
              await fetch(`${API_URL}/ai/generate-alerts?platform=GOOGLE`, { method: 'POST' });
              addToast('AI alerts generated', 'success');
            } catch (_) { addToast('Alert generation failed', 'error'); }
          }
        }}
      />

      {/* Keyboard shortcut chord hint — visible while 'g' is the pending key */}
      {pendingKey === 'g' && (
        <div className="fixed bottom-24 lg:bottom-6 right-4 z-50 bg-[#09090B] border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-400 font-mono shadow-2xl animate-fade-in pointer-events-none">
          <span className="text-zinc-500 mr-1">g →</span>
          <span className="text-white mr-2">h</span><span className="text-zinc-500 mr-3">Overview</span>
          <span className="text-white mr-2">a</span><span className="text-zinc-500 mr-3">Alerts</span>
          <span className="text-white mr-2">m</span><span className="text-zinc-500 mr-3">Meta</span>
          <span className="text-white mr-2">g</span><span className="text-zinc-500">Google</span>
        </div>
      )}

      {/* AI Chat Window */}
      <AIChatWindow
        accounts={[
          ...(data?.accounts || []).filter(a => a.platform === 'META').map(a => ({ id: a.id, name: a.name, platform: 'meta' })),
          ...(data?.accounts || []).filter(a => a.platform === 'GOOGLE').map(a => ({ id: a.id, name: a.name, platform: 'google' }))
        ]}
        addToast={addToast}
      />
    </div>
  );
};

export default Dashboard;

